/**
 * The browser side of device location (ADR-43): reading a position only when
 * the user asked for it, and remembering their choice on this browser.
 *
 * Nothing here runs on its own. Every call starts from a click: "Share
 * location", "Stop sharing", or opening a project once sharing is on.
 */

/** What one read of the device position came to. */
export type PositionOutcome =
  | {
      readonly kind: 'position';
      readonly latitude: number;
      readonly longitude: number;
      readonly accuracy: number;
    }
  | { readonly kind: 'denied' | 'unavailable' | 'timeout' | 'unsupported' };

/** The browser's geolocation permission for this site, when it will say. */
export type GeolocationPermission = 'granted' | 'denied' | 'prompt' | 'unknown';

/**
 * The user's choice on this browser:
 * - `on`: they chose to share; refreshed when they open a project.
 * - `off`: they turned it off.
 * - `dismissed`: "Not now"; the banner is not shown again.
 * - `denied`: the browser refused; never asked again unless they choose to.
 */
export type LocationPreference = 'on' | 'off' | 'dismissed' | 'denied';

const PREFERENCES: readonly LocationPreference[] = ['on', 'off', 'dismissed', 'denied'];

/** Coarse is enough for a city, and much faster and kinder to the battery. */
const POSITION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 10_000,
  maximumAge: 10 * 60_000,
};

export function isGeolocationSupported(): boolean {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator;
}

/**
 * Reads the device position once. May show the browser's permission prompt,
 * so call it only from a user action. Never rejects.
 */
export function readDevicePosition(
  options: PositionOptions = POSITION_OPTIONS,
): Promise<PositionOutcome> {
  if (!isGeolocationSupported()) {
    return Promise.resolve({ kind: 'unsupported' });
  }
  return new Promise((resolve) => {
    try {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            kind: 'position',
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          resolve({
            kind:
              error.code === error.PERMISSION_DENIED
                ? 'denied'
                : error.code === error.TIMEOUT
                  ? 'timeout'
                  : 'unavailable',
          });
        },
        options,
      );
    } catch {
      resolve({ kind: 'unavailable' });
    }
  });
}

/** Asks the Permissions API without prompting; `unknown` where it cannot say. */
export async function geolocationPermission(): Promise<GeolocationPermission> {
  try {
    if (typeof navigator === 'undefined' || !('permissions' in navigator)) {
      return 'unknown';
    }
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state;
  } catch {
    return 'unknown';
  }
}

/** Per user, so a shared computer never carries one person's choice to another. */
function preferenceKey(userId: string): string {
  return `prisco.device-location.${userId}`;
}

export function readLocationPreference(userId: string): LocationPreference | null {
  try {
    const stored = window.localStorage.getItem(preferenceKey(userId));
    return PREFERENCES.find((value) => value === stored) ?? null;
  } catch {
    return null;
  }
}

export function writeLocationPreference(userId: string, preference: LocationPreference): void {
  try {
    window.localStorage.setItem(preferenceKey(userId), preference);
  } catch {
    // Private mode or blocked storage: the choice lasts for this page only.
  }
}
